import { sendAnalytics } from '@audio-network/analytics'
import {
	Card,
	CardList,
	Container,
	Heading,
	Paragraph,
} from '@audio-network/componentlibrary'
import { Button } from '@audio-network/componentlibrary'
import { Related } from '@audio-network/componentlibrary/icons'
import { usePlayer } from '@audio-network/player'
import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { GetServerSideProps } from 'next/types'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useState } from 'react'

import { clientGetSimilarTracks } from '@/client'
import Header from '@/components/pages/playlist/Header/Header'
import TrackList from '@/components/shared/TrackList/TrackList'
import { CARD_LIST_THUMBNAIL_MAX_WIDTH } from '@/constants/sizing'
import { FIGARO, PLAYLIST } from '@/constants/trackSources'
import { getTracks } from '@/pages/api/musicapp/_services/musicService'
import getPlaylist from '@/pages/api/musicapp/_services/playlistService'
import { getCurrentSession } from '@/pages/api/musicapp/_services/sessionService'
import optimizeContentfulImage from '@/utils/contentful/optimizeContentfulImage'
import formatTitleByLocale from '@/utils/formatting/formatTitleByLocale'
import { withGlobalTranslations } from '@/utils/i18n/withGlobalTranslations'

import styles from './index.module.css'

export default function PlaylistPage({
	locale,
	playlist,
	relatedTracks,
	tracks,
	isLoggedIn,
	isCatalogueAvailable,
}: {
	locale: string
	playlist: Playlist
	relatedTracks: Track[]
	tracks: Track[]
	isLoggedIn: boolean
	isCatalogueAvailable: boolean
}): JSX.Element {
	const { initialiseQueue, togglePlay, isPlaying, currentTrack } = usePlayer()

	const { t } = useTranslation(['playlist'])

	const router = useRouter()
	const { asPath } = router

	const [similarTracks, setSimilarTracks] = useState(null)
	const [isLoadingSimilarTracks, setIsLoadingSimilarTracks] = useState(false)

	useEffect(() => {
		setSimilarTracks(null)
		setIsLoadingSimilarTracks(false)

		sendAnalytics({
			noun: 'Playlist',
			pastTenseVerb: 'Page Viewed',
			eventParameters: {
				Title: playlist.title,
			},
		})
	}, [asPath, playlist.title])

	useEffect(() => {
		if (tracks) {
			initialiseQueue([
				...tracks,
				...(similarTracks || []),
				...(relatedTracks || []),
			])
		}
	}, [tracks, similarTracks, relatedTracks, initialiseQueue])

	const handleRelatedPlaylistClick = (name: string, urlSlug: string): void => {
		sendAnalytics({
			noun: 'Playlist',
			pastTenseVerb: 'Clicked',
			eventParameters: {
				Name: name,
				UrlSlug: urlSlug,
			},
		})
	}

	const getTrack = (playlist: Playlist): Track => {
		const firstTrackId = parseInt(playlist.trackIds.split(',')[0])
		const track = relatedTracks?.find((x) => x?.id === firstTrackId)
		return track
	}

	const handleSimilarTracksClicked = async (): Promise<void> => {
		setIsLoadingSimilarTracks(true)

		sendAnalytics({
			noun: 'Similar Tracks',
			pastTenseVerb: 'Clicked',
		})

		const trackIds: number[] = tracks.map((track) => {
			return track.id
		})

		if (trackIds.length > 100) trackIds.length = 100

		const results = await clientGetSimilarTracks(trackIds)

		setSimilarTracks(results)

		if (results?.length > 0) {
			sendAnalytics({
				noun: 'Similar Tracks',
				pastTenseVerb: 'Completed',
			})
		}
	}

	const showSimilarTracks = isLoggedIn && playlist?.similarTracksEnabled

	return (
		<>
			<Head>
				<title>{formatTitleByLocale(locale, playlist.title)}</title>
			</Head>
			<Header playlist={playlist} />

			{tracks?.length > 0 && (
				<>
					<Container maxWidth="large">
						<TrackList
							download={{
								canDownloadAll: true,
								name: playlist.title,
							}}
							isCatalogueAvailable={isCatalogueAvailable}
							tracks={tracks}
							source={PLAYLIST}
							skipQueueInitialisation
							data-testid="playlist-tracklist"
						/>

						{showSimilarTracks &&
							(similarTracks ? (
								<>
									<div className={styles.similarTracksHeadingWrapper}>
										<Heading level={2} size="S" uppercase>
											{t('similar-tracks-heading')}
										</Heading>
										{similarTracks.length === 0 && (
											<Paragraph>{t('similar-tracks-none-found')}</Paragraph>
										)}
									</div>
									<TrackList
										tracks={similarTracks}
										source={FIGARO}
										skipQueueInitialisation
										data-testid="playlist-similar-tracklist"
									/>
								</>
							) : (
								<div className={styles.similarTracksButtonWrapper}>
									<Button
										variant="secondary"
										startIcon={<Related />}
										onClick={() => handleSimilarTracksClicked()}
										spinner={isLoadingSimilarTracks}
									>
										{t('similar-tracks-button')}
									</Button>
								</div>
							))}
					</Container>

					{playlist.relatedPlaylists?.length > 0 && (
						<Container maxWidth="large">
							<Heading level={2} size="S" uppercase>
								{t('related-playlists')}
							</Heading>
							<CardList
								data-testid="related-playlists"
								scrollable
								key={playlist.urlSlug}
							>
								{playlist.relatedPlaylists.map((relatedPlaylist) => {
									const track = getTrack(relatedPlaylist)

									const isRelatedTrackPlaying =
										isPlaying && track?.id === currentTrack?.id

									return (
										<Card
											key={relatedPlaylist.urlSlug}
											href={relatedPlaylist.urlSlug}
											title={relatedPlaylist.title}
											description={relatedPlaylist.description}
											onClick={() => {
												handleRelatedPlaylistClick(
													relatedPlaylist.title,
													relatedPlaylist.urlSlug
												)
											}}
											buttonProps={{
												'aria-label': isRelatedTrackPlaying ? 'Pause' : 'Play',
											}}
											isPlaying={isRelatedTrackPlaying}
											togglePlay={() =>
												togglePlay(track, { source: 'Playlist' })
											}
											thumbnail={
												<Image
													alt=""
													src={optimizeContentfulImage(
														relatedPlaylist.thumbnail?.url ??
															relatedPlaylist.background.url,
														{
															width: CARD_LIST_THUMBNAIL_MAX_WIDTH,
															height: CARD_LIST_THUMBNAIL_MAX_WIDTH,
														}
													)}
													height={CARD_LIST_THUMBNAIL_MAX_WIDTH}
													width={CARD_LIST_THUMBNAIL_MAX_WIDTH}
													sizes="(min-width: 75em)20vw, 100vw"
												/>
											}
										/>
									)
								})}
							</CardList>
						</Container>
					)}
				</>
			)}
		</>
	)
}

export const getServerSideProps: GetServerSideProps = async ({
	locale,
	query,
	res,
	req,
}) => {
	const { slug } = query

	const playlist = await getPlaylist(slug as string, locale)

	if (!playlist) {
		return {
			notFound: true,
		}
	}

	const user: User = await getCurrentSession(req, res)

	const tracks =
		playlist.trackIds && (await getTracks(playlist.trackIds.split(',')))

	let isCatalogueAvailable = true
	if (user) {
		isCatalogueAvailable = tracks.every((track) =>
			user.viewableCatalogues.includes(track.album.catalogue.id)
		)
	}

	const relatedTracks =
		(await getTracks(
			playlist.relatedPlaylists.map(
				(relatedPlaylist) => relatedPlaylist.trackIds?.split(',')[0]
			)
		)) ?? null

	return {
		props: {
			...(await withGlobalTranslations(locale, ['playlist', 'trackrow'])),
			locale,
			playlist,
			relatedTracks,
			tracks,
			isLoggedIn: !!user,
			isCatalogueAvailable,
		},
	}
}
